import { Field } from '@base-ui/react';
import { Popover as BasePopover } from '@base-ui/react/popover';
import {
  type ChangeEvent,
  type FC,
  memo,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import CheckIcon from '@/assets/svg/ui-elements/check-outline.svg';
import ChevronDownIcon from '@/assets/svg/ui-elements/chevron-down.svg';
import SearchIcon from '@/assets/svg/ui-elements/search-lg.svg';
import { COUNTRIES, type Country } from '@/constants/countries-phones';
import {
  type PhoneNumberFieldChange,
  usePhoneNumberField,
} from '@/global-hooks/use-phone-number-field';
import { detectBrowserCountry } from '@/utils/detect-browser-country';
import { findCountryByIso2 } from '@/utils/find-country-by-iso2';
import { parsePhoneNumber } from '@/utils/parse-phone-number';

import HelperDescription from '../Helper/HelperDescription';
import Input from '../Input';
import { StyledBox, StyledIconWrapper, StyledInput, StyledRoot } from '../Input/Input.styled';
import type { InputSize } from '../Input/types';
import Label from '../Label';
import Typography from '../Typography';
import {
  StyledChevron,
  StyledCountryItem,
  StyledCountryList,
  StyledCountryTrigger,
  StyledDivider,
  StyledEmpty,
  StyledFlag,
  StyledItemFlag,
  StyledPopup,
  StyledPositioner,
} from './InputPhone.styled';
import type { InputPhoneProps, InputPhoneSize, InputPhoneValue } from './types';

// The Figma `input phone` grid (32 / 40 / 48) is a subset of the Input grid (32 / 36 / 40 / 48).
const INPUT_SIZE: Record<InputPhoneSize, InputSize> = { sm: 'sm', md: 'lg', lg: 'xl' };

const getFlagSrc = (iso2: string) => `/flags/${iso2.toLowerCase()}.svg`;

const getInitialCountry = (
  value: string,
  countryIso2: string | null | undefined,
): Country | null => {
  const parsed = parsePhoneNumber(value);
  if (parsed.country) return parsed.country;
  return findCountryByIso2(countryIso2) ?? detectBrowserCountry() ?? null;
};

const toPhoneValue = (
  country: Country | null,
  digits: string,
  nationalNumber: string,
): InputPhoneValue => ({
  countryIso2: country?.iso2 ?? null,
  dialCode: country?.dialCode ?? null,
  nationalNumber,
  e164: country ? `+${country.dialCode}${nationalNumber}` : digits,
});

const InputPhone: FC<InputPhoneProps> = memo(
  ({
    value = '',
    onChange,
    countryIso2,
    size = 'md',
    label,
    required,
    optional,
    tooltip,
    topRightLabel,
    helperText,
    state = 'default',
    iconStart,
    placeholder = '',
    disabled = false,
    name,
    endIcon,
    searchPlaceholder = 'Country or code',
    noResultsMessage = 'No results found',
    ariaLabel,
    containerClassName,
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(() =>
      getInitialCountry(value, countryIso2),
    );
    const [localNumber, setLocalNumber] = useState(() => parsePhoneNumber(value).localNumber);
    const shellRef = useRef<HTMLDivElement>(null);
    const searchWrapperRef = useRef<HTMLDivElement>(null);
    const hasUserPickedCountryRef = useRef(parsePhoneNumber(value).country !== null);
    const selectedCountryRef = useRef(selectedCountry);
    const nationalNumberRef = useRef('');
    const lastEmittedValueRef = useRef(value);

    const emit = useCallback(
      (next: InputPhoneValue) => {
        lastEmittedValueRef.current = next.e164;
        onChange(next);
      },
      [onChange],
    );

    useEffect(() => {
      selectedCountryRef.current = selectedCountry;
    }, [selectedCountry]);

    useEffect(() => {
      // Our own emit carries the metadata national number, which drops a trunk prefix the user
      // typed; echoing it back into state would erase that digit mid-typing.
      if (value === lastEmittedValueRef.current) return;
      lastEmittedValueRef.current = value;
      const parsed = parsePhoneNumber(value);
      setLocalNumber(parsed.localNumber);
      if (!parsed.country) return;
      hasUserPickedCountryRef.current = true;
      const current = selectedCountryRef.current;
      if (!current || current.dialCode !== parsed.country.dialCode) {
        setSelectedCountry(parsed.country);
      }
    }, [value]);

    useEffect(() => {
      if (hasUserPickedCountryRef.current) return;
      const next = findCountryByIso2(countryIso2);
      if (!next || selectedCountryRef.current?.iso2 === next.iso2) return;
      setSelectedCountry(next);
      emit(toPhoneValue(next, nationalNumberRef.current, nationalNumberRef.current));
    }, [countryIso2, emit]);

    const filteredCountries = useMemo(() => {
      const query = searchQuery.trim().toLowerCase();
      if (!query) return COUNTRIES;
      return COUNTRIES.filter(
        (country) => country.name.toLowerCase().includes(query) || country.dialCode.includes(query),
      );
    }, [searchQuery]);

    const closePicker = useCallback(() => {
      setIsOpen(false);
      setSearchQuery('');
    }, []);

    const handleCountrySelect = useCallback(
      (country: Country) => {
        hasUserPickedCountryRef.current = true;
        setSelectedCountry(country);
        closePicker();
        emit(toPhoneValue(country, nationalNumberRef.current, nationalNumberRef.current));
      },
      [closePicker, emit],
    );

    const applyPhoneChange = useCallback(
      ({ digits, nationalNumber: nextNationalNumber, country }: PhoneNumberFieldChange) => {
        if (country) {
          hasUserPickedCountryRef.current = true;
          setSelectedCountry(country);
        }
        const targetCountry = country ?? selectedCountry;
        setLocalNumber(digits);
        emit(
          digits
            ? toPhoneValue(targetCountry, digits, nextNationalNumber)
            : { ...toPhoneValue(targetCountry, '', ''), e164: '' },
        );
      },
      [emit, selectedCountry],
    );

    const {
      displayValue,
      nationalNumber,
      onChange: handlePhoneChange,
    } = usePhoneNumberField({
      digits: localNumber,
      iso2: selectedCountry?.iso2,
      onValueChange: applyPhoneChange,
    });

    nationalNumberRef.current = nationalNumber;

    const handleShellMouseDown = (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-phone-chevron]')) {
        event.preventDefault();
        if (!disabled) setIsOpen((open) => !open);
        return;
      }
      if (target !== event.currentTarget) return;
      event.preventDefault();
      event.currentTarget.querySelector('input')?.focus();
    };

    return (
      <StyledRoot
        disabled={disabled}
        invalid={state === 'destructive'}
        className={containerClassName}
        name={name}
      >
        {label && (
          <Field.Label
            render={
              <Label
                required={required}
                optional={optional}
                tooltip={tooltip}
                topRightLabel={topRightLabel}
              >
                {label}
              </Label>
            }
          />
        )}
        <StyledBox
          ref={shellRef}
          $size={INPUT_SIZE[size]}
          $state={state}
          onMouseDown={handleShellMouseDown}
        >
          <StyledCountryTrigger
            type="button"
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-label={`Country code${selectedCountry ? `, +${selectedCountry.dialCode}` : ''}`}
            onClick={() => setIsOpen((open) => !open)}
          >
            {selectedCountry && <StyledFlag src={getFlagSrc(selectedCountry.iso2)} alt="" />}
            {selectedCountry ? `+${selectedCountry.dialCode}` : '+'}
          </StyledCountryTrigger>
          <StyledDivider aria-hidden />
          <StyledInput
            $size={INPUT_SIZE[size]}
            value={displayValue}
            onChange={handlePhoneChange}
            placeholder={placeholder}
            disabled={disabled}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            aria-label={label ? undefined : (ariaLabel ?? 'Phone number')}
          />
          {endIcon && <StyledIconWrapper>{endIcon}</StyledIconWrapper>}
          <StyledChevron $open={isOpen} data-phone-chevron aria-hidden>
            <ChevronDownIcon />
          </StyledChevron>
        </StyledBox>
        <HelperDescription state={state} iconStart={iconStart} label={helperText} />

        <BasePopover.Root
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) closePicker();
          }}
        >
          <BasePopover.Portal>
            <StyledPositioner anchor={shellRef} side="bottom" align="start" sideOffset={8}>
              <StyledPopup
                initialFocus={() => searchWrapperRef.current?.querySelector('input') ?? null}
                aria-label="Choose a country code"
              >
                <div ref={searchWrapperRef}>
                  <Input
                    size="sm"
                    value={searchQuery}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setSearchQuery(event.target.value)
                    }
                    placeholder={searchPlaceholder}
                    startIcon={<SearchIcon />}
                  />
                </div>
                {filteredCountries.length === 0 ? (
                  <StyledEmpty role="status">{noResultsMessage}</StyledEmpty>
                ) : (
                  <StyledCountryList>
                    {filteredCountries.map((country) => {
                      const isSelected = selectedCountry?.iso2 === country.iso2;
                      return (
                        <StyledCountryItem
                          key={country.iso2}
                          type="button"
                          aria-current={isSelected || undefined}
                          onClick={() => handleCountrySelect(country)}
                        >
                          <StyledItemFlag src={getFlagSrc(country.iso2)} alt="" />
                          <Typography variant="body2" as="span">
                            +{country.dialCode}
                          </Typography>
                          <Typography variant="body2" as="span" noWrap>
                            {country.name}
                          </Typography>
                          {isSelected && <CheckIcon />}
                        </StyledCountryItem>
                      );
                    })}
                  </StyledCountryList>
                )}
              </StyledPopup>
            </StyledPositioner>
          </BasePopover.Portal>
        </BasePopover.Root>
      </StyledRoot>
    );
  },
);

InputPhone.displayName = 'InputPhone';

export default InputPhone;
